import { FC, useState, useEffect } from "react";
import NInputNumber from "./NInputNumber";
import { Button, Divider, Modal, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  useDeleteItemInCart,
  useUpdateItemInCart,
  useUpdateRedemptionItemInCart,
} from "services/cart";
import { IMAGE_NOT_FOUND } from "constants/common";
import { RedemptionItem } from "types/redemption";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "utils/debounce";
import ProductImage from "components/ProductImage";

interface ICartProductItem {
  itemId: number;
  url: string;
  name: string;
  price: string;
  qty: number;
  productId: number;
  productCode?: string;
  isRedemptionItem?: boolean;
  redemptionItem?: RedemptionItem;
}
const CartProductItem: FC<ICartProductItem> = ({
  itemId,
  url,
  name,
  price,
  qty,
  productId,
  productCode,
  isRedemptionItem = false,
  redemptionItem,
}) => {
  const [quantity, setQuantity] = useState(qty);
  const [totalPrice, setTotalPrice] = useState(Number(price) * quantity);
  const { deleteItemInCartAsync, isPending } = useDeleteItemInCart();
  const { updateItemInCartAsync } = useUpdateItemInCart();
  const { updateRedemptionItemInCartAsync } = useUpdateRedemptionItemInCart();
  const navigate = useNavigate();

  useEffect(() => {
    setQuantity(qty);
    setTotalPrice(Number(price) * qty);
  }, [price, qty]);

  const onDeleteItem = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteItemInCartAsync(itemId);
  };

  const [isModalVisible, setIsModalVisible] = useState(false);

  const debouncedUpdateCart = useDebounce(
    (numericValue: number) => {
      if (isRedemptionItem) {
        updateRedemptionItemInCartAsync({
          cartItemId: itemId,
          quantity: numericValue,
        });
      } else {
        updateItemInCartAsync({
          cartItemId: itemId,
          quantity: numericValue,
        });
      }
    },
    500, // 500ms debounce time
    [
      itemId,
      updateItemInCartAsync,
      updateRedemptionItemInCartAsync,
      isRedemptionItem,
    ]
  );

  const handleOnChangeInput = (value: number) => {
    if (value === 0) {
      setIsModalVisible(true);
      return;
    }

    // Update local state immediately for UI
    setQuantity(value);
    setTotalPrice(Number(price) * value);

    // Debounce the API call
    debouncedUpdateCart(value);
  };

  const handleModalConfirm = async (e: React.MouseEvent) => {
    // User confirmed deletion
    e.stopPropagation();
    await onDeleteItem(e);
    setIsModalVisible(false);
  };

  const handleModalCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuantity(1);
    setTotalPrice(Number(price) * 1);

    if (isRedemptionItem) {
      updateRedemptionItemInCartAsync({
        cartItemId: itemId,
        quantity: 1,
      });
    } else {
      updateItemInCartAsync({
        cartItemId: itemId,
        quantity: 1,
      });
    }

    setIsModalVisible(false);
  };

  return (
    <div
      onClick={() => {
        // Don't navigate for redemption items, or navigate to capsule market
        if (!isRedemptionItem && productId) {
          navigate("/product/" + productId);
        } else if (isRedemptionItem) {
          navigate("/capsule-market");
        }
      }}
      className="cursor-pointer"
    >
      <div className="flex justify-between">
        <div className="flex gap-4">
          <ProductImage
            imageUrl={url}
            productCode={productCode}
            width={90}
            height={90}
            alt=""
            className="object-contain"
            fallback={IMAGE_NOT_FOUND}
          />
          <div className="flex flex-col justify-between">
            <div className="w-[320px] line-clamp-2 break-words">{name}</div>
            <NInputNumber
              value={quantity}
              onChange={(e) => handleOnChangeInput(e)}
              disabled={false} // Enable quantity change for all items including redemption items
            />
          </div>
        </div>
        <div className="flex flex-col justify-between">
          <div className="font-semibold">
            {isRedemptionItem ? (
              <div>
                <div className="text-primary-500">
                  {redemptionItem?.pointsCost} คะแนน
                </div>
                {redemptionItem?.type === "HYBRID" && (
                  <div className="text-sm">
                    + {totalPrice.toLocaleString()} บาท
                  </div>
                )}
              </div>
            ) : (
              <div>{totalPrice.toLocaleString()} บาท</div>
            )}
          </div>
          <div className="flex justify-center">
            <Popconfirm
              title="ลบสินค้า"
              description="คุณต้องการลบสินค้านี้ออกจากตะกร้าหรือไม่?"
              onConfirm={(e?: React.MouseEvent<HTMLElement>) => {
                e?.stopPropagation();
                deleteItemInCartAsync(itemId);
              }}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Button
                type="text"
                danger
                loading={isPending}
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </div>
        </div>
      </div>
      <Divider />

      {/* Confirmation Modal */}
      <Modal
        title="คุณแน่ใจว่าต้องการลบหรือไม่?"
        open={isModalVisible}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}
        okText="ใช่"
        cancelText="ไม่"
        okButtonProps={{
          size: "large",
          className: "px-8 h-10",
        }}
        cancelButtonProps={{
          size: "large",
          danger: true,
          className: "px-8 h-10",
        }}
      >
        <p>คุณต้องการลบสินค้านี้ออกจากตะกร้าหรือไม่?</p>
      </Modal>
    </div>
  );
};
export default CartProductItem;
